/*
 * Portions of this component's visual styling are adapted from shadcn/ui.
 * Copyright (c) 2023 shadcn. Licensed under the MIT License.
 */

import { AppButton } from "./app-button.js";

function respond(host: HTMLElement, confirmed: boolean): void {
  host.dispatchEvent(
    new CustomEvent("response", { bubbles: true, composed: true, detail: { confirmed: confirmed } }),
  );
}

const styles = css`
  :host {
    display: contents;
  }

  .overlay {
    align-items: center;
    background: rgb(0 0 0 / 50%);
    inset: 0;
    justify-content: center;
    padding: 16px;
    position: fixed;
    z-index: 50;
  }

  .dialog {
    background: var(--background, #ffffff);
    border: 1px solid var(--border, #e4e4e7);
    border-radius: var(--radius-lg, 10px);
    box-shadow: 0 10px 38px rgb(0 0 0 / 18%);
    box-sizing: border-box;
    color: var(--foreground, #09090b);
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-width: 512px;
    padding: 24px;
    width: 100%;
  }

  .title {
    font-size: 1.05rem;
    font-weight: 600;
    line-height: 1.3;
  }

  .description {
    color: var(--muted-foreground, #71717a);
    font-size: 0.875rem;
    line-height: 1.45;
  }

  .footer {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
    margin-top: 12px;
  }

  app-button { min-width: 72px; }
`;

export class AppAlertDialog extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppAlertDialog)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["open","data-open","title","data-title","description","data-description","cancellabel","data-cancel-label","actionlabel","data-action-label","variant","data-variant"];

  #open = false;
  #title = "Are you sure?";
  #description = "";
  #cancelLabel = "Cancel";
  #actionLabel = "Continue";
  #variant = "default";
  #openSet = false;
  #titleSet = false;
  #descriptionSet = false;
  #cancelLabelSet = false;
  #actionLabelSet = false;
  #variantSet = false;
  #textStart0;
  #textEnd0;
  #textLast0;
  #textStart1;
  #textEnd1;
  #textLast1;
  #textStart2;
  #textEnd2;
  #textLast2;
  #textStart3;
  #textEnd3;
  #textLast3;
  #attr0;
  #attr1;
  #attrLast0;
  #attrLast1;
  #hooksLive = false;
  #cleanups = [];
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get open() {
    return this.#open;
  }

  set open(value) {
    const raw = value ?? false;
    const next = raw == null ? raw : typeof raw === 'string' ? raw !== 'false' : Boolean(raw);
    if (Object.is(this.#open, next)) return;
    this.#open = next;
    this.#openSet = true;
    this.#updateBindings('open');
  }

  get title() {
    return this.#title;
  }

  set title(value) {
    const raw = value ?? "Are you sure?";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#title, next)) return;
    this.#title = next;
    this.#titleSet = true;
    this.#updateBindings('title');
  }

  get description() {
    return this.#description;
  }

  set description(value) {
    const raw = value ?? "";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#description, next)) return;
    this.#description = next;
    this.#descriptionSet = true;
    this.#updateBindings('description');
  }

  get cancelLabel() {
    return this.#cancelLabel;
  }

  set cancelLabel(value) {
    const raw = value ?? "Cancel";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#cancelLabel, next)) return;
    this.#cancelLabel = next;
    this.#cancelLabelSet = true;
    this.#updateBindings('cancelLabel');
  }

  get actionLabel() {
    return this.#actionLabel;
  }

  set actionLabel(value) {
    const raw = value ?? "Continue";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#actionLabel, next)) return;
    this.#actionLabel = next;
    this.#actionLabelSet = true;
    this.#updateBindings('actionLabel');
  }

  get variant() {
    return this.#variant;
  }

  set variant(value) {
    const raw = value ?? "default";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#variant, next)) return;
    this.#variant = next;
    this.#variantSet = true;
    this.#updateBindings('variant');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-open')) this.open = this.dataset.open;
    if (this.hasAttribute('data-title')) this.title = this.dataset.title;
    if (this.hasAttribute('data-description')) this.description = this.dataset.description;
    if (this.hasAttribute('data-cancel-label')) this.cancelLabel = this.dataset.cancelLabel;
    if (this.hasAttribute('data-action-label')) this.actionLabel = this.dataset.actionLabel;
    if (this.hasAttribute('data-variant')) this.variant = this.dataset.variant;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.open = props.open;
      this.title = props.title;
      this.description = props.description;
      this.cancelLabel = props.cancelLabel;
      this.actionLabel = props.actionLabel;
      this.variant = props.variant;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (!this.#isCSR && !this.#bindingsMounted) {
      this.#syncHydratedProps();
    if (!this.#openSet) this.open = this.dataset.open;
    if (!this.#titleSet) this.title = this.dataset.title;
    if (!this.#descriptionSet) this.description = this.dataset.description;
    if (!this.#cancelLabelSet) this.cancelLabel = this.dataset.cancelLabel;
    if (!this.#actionLabelSet) this.actionLabel = this.dataset.actionLabel;
    if (!this.#variantSet) this.variant = this.dataset.variant;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
    }
    if (this.#hooksLive) return;
    this.#hooksLive = true;
    {
      const cleanup = (() => {
    const onKey = (event: KeyboardEvent) => {
        if (event.key === "Escape" && this.#open)
            respond(this, false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
})();
      if (typeof cleanup === 'function') this.#cleanups.push(cleanup);
    }
  }

  disconnectedCallback() {
    this.#hooksLive = false;
    for (const cleanup of this.#cleanups.splice(0)) cleanup();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'open':
      case 'data-open':
        this.open = newValue;
        break;
      case 'title':
      case 'data-title':
        this.title = newValue;
        break;
      case 'description':
      case 'data-description':
        this.description = newValue;
        break;
      case 'cancellabel':
      case 'data-cancel-label':
        this.cancelLabel = newValue;
        break;
      case 'actionlabel':
      case 'data-action-label':
        this.actionLabel = newValue;
        break;
      case 'variant':
      case 'data-variant':
        this.variant = newValue;
        break;
    }
  }

  #initState() {

  }

  #render() {
    
    return `<style>${this.#escText(styles)}</style><div class="overlay" style="${this.#escAttr(this.#open ? "display:flex" : "display:none")}" data-argon-bind="0"><div class="dialog" role="alertdialog" aria-modal="true"><div class="title"><!--argon-text:0-->${this.#escText(this.#title)}<!--/argon-text:0--></div><div class="description"><!--argon-text:1-->${this.#escText(this.#description)}<!--/argon-text:1--></div><div class="footer"><app-button class="cancel" variant="outline" data-argon="2368"><!--argon-text:2-->${""}${this.#escText(this.#cancelLabel)}<!--/argon-text:2--></app-button><app-button class="action" variant="${this.#escAttr((this.#variant === "destructive") ? "destructive" : "default")}" data-argon="2561" data-argon-bind="1"><!--argon-text:3-->${""}${this.#escText(this.#actionLabel)}<!--/argon-text:3--></app-button></div></div></div>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
        case 'argon-text:1':
          this.#textStart1 = marker;
          break;
        case '/argon-text:1':
          this.#textEnd1 = marker;
          break;
        case 'argon-text:2':
          this.#textStart2 = marker;
          break;
        case '/argon-text:2':
          this.#textEnd2 = marker;
          break;
        case 'argon-text:3':
          this.#textStart3 = marker;
          break;
        case '/argon-text:3':
          this.#textEnd3 = marker;
          break;
      }
    }
    this.#attr0 = root.querySelector('[data-argon-bind="0"]');
    this.#attr1 = root.querySelector('[data-argon-bind="1"]');
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }
  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escAttr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      handler.call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    
    if (prop === undefined || prop === 'title') {
      const __argonNext = `${this.#escText(this.#title)}`;
      if (__argonNext !== this.#textLast0) {
        this.#textLast0 = __argonNext;
        this.#replaceRange(this.#textStart0, this.#textEnd0, __argonNext);
      }
    }
    if (prop === undefined || prop === 'description') {
      const __argonNext = `${this.#escText(this.#description)}`;
      if (__argonNext !== this.#textLast1) {
        this.#textLast1 = __argonNext;
        this.#replaceRange(this.#textStart1, this.#textEnd1, __argonNext);
      }
    }
    if (prop === undefined || prop === 'cancelLabel') {
      const __argonNext = `${""}${this.#escText(this.#cancelLabel)}`;
      if (__argonNext !== this.#textLast2) {
        this.#textLast2 = __argonNext;
        this.#replaceRange(this.#textStart2, this.#textEnd2, __argonNext);
      }
    }
    if (prop === undefined || prop === 'actionLabel') {
      const __argonNext = `${""}${this.#escText(this.#actionLabel)}`;
      if (__argonNext !== this.#textLast3) {
        this.#textLast3 = __argonNext;
        this.#replaceRange(this.#textStart3, this.#textEnd3, __argonNext);
      }
    }
    if ((prop === undefined || prop === 'open') && this.#attr0) {
      const __argonNext = `${this.#open ? "display:flex" : "display:none"}`;
      if (__argonNext !== this.#attrLast0) {
        this.#attrLast0 = __argonNext;
        this.#attr0.setAttribute('style', __argonNext);
      }
    }
    if ((prop === undefined || prop === 'variant') && this.#attr1) {
      const __argonNext = `${(this.#variant === "destructive") ? "destructive" : "default"}`;
      if (__argonNext !== this.#attrLast1) {
        this.#attrLast1 = __argonNext;
        this.#attr1.setAttribute('variant', __argonNext);
      }
    }
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '2368').forEach(el => this.#listen(el, '2368', 'click', () => respond(this, false)));
    this.#eventTargets(scope, '2561').forEach(el => this.#listen(el, '2561', 'click', () => respond(this, true)));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
